import { getTierFieldsToClearOnTopTierChange } from '../TrackerViewerV2/utils';
import { evaluateAllConditions } from './config/evaluateCondition';
import { isConfigEnabled } from './config/isConfigEnabled';
import { isIdmApiEnabled } from './config/resolveBehaviorConfig';

export const TOP_TIER_REQUIRED_FIELD = 'topTierRequired';

/**
 * Returns configured trigger fields for tier auto-calculation.
 */
export const getTierTriggerFields = (behaviorConfig) => (
    behaviorConfig?.tier?.autoCalculation?.triggerFields || ['gcmTeam', 'dealValue']
);

/**
 * Returns true when trigger fields are present and dealValue is numeric.
 */
export const isTierAutoCalcInputReady = (state = {}, behaviorConfig) => {
    const triggerFields = getTierTriggerFields(behaviorConfig);

    return triggerFields.every((fieldKey) => {
        const value = state?.[fieldKey];

        if (fieldKey === 'dealValue') {
            if (value === '' || value == null || value === undefined) {
                return false;
            }

            return !Number.isNaN(Number(value));
        }

        return value != null && String(value).trim() !== '';
    });
};

/**
 * Evaluates the first matching enabled auto-calculation rule.
 */
export const evaluateTierAutoCalculation = (state = {}, behaviorConfig) => {
    const autoCalculation = behaviorConfig?.tier?.autoCalculation;

    if (
        !isConfigEnabled(behaviorConfig)
        || !isConfigEnabled(behaviorConfig?.tier)
        || !isConfigEnabled(autoCalculation)
        || !isTierAutoCalcInputReady(state, behaviorConfig)
    ) {
        return { result: undefined, belowThreshold: false };
    }

    const enabledRules = (autoCalculation.rules || []).filter((rule) => isConfigEnabled(rule));

    for (const rule of enabledRules) {
        if (evaluateAllConditions(state, rule.conditions)) {
            return {
                result: rule.result,
                belowThreshold: rule.belowThreshold === true,
            };
        }
    }

    return { result: null, belowThreshold: false };
};

/**
 * Derives topTierRequired from configured tier rules.
 */
export const calculateTopTierRequiredFromState = (state = {}, behaviorConfig) => {
    const { result } = evaluateTierAutoCalculation(state, behaviorConfig);
    return result === undefined ? null : result;
};

/**
 * True when the row matches a below-threshold auto-calculation rule.
 */
export const isDealBelowTrackerThreshold = (state = {}, behaviorConfig) => {
    const { belowThreshold } = evaluateTierAutoCalculation(state, behaviorConfig);
    return belowThreshold;
};

/**
 * Returns alert copy when the deal is below the tracker threshold, or null when allowed.
 */
export const getBelowTrackerThresholdError = (state = {}, behaviorConfig) => {
    const belowThresholdConfig = behaviorConfig?.tier?.autoCalculation?.belowThreshold;

    if (
        !isConfigEnabled(belowThresholdConfig)
        || !isDealBelowTrackerThreshold(state, behaviorConfig)
    ) {
        return null;
    }

    const messageField = belowThresholdConfig.messageField || 'gcmTeam';
    const lookupKey = state?.[messageField];
    const messageConfig = belowThresholdConfig.messages?.[lookupKey];

    if (!messageConfig) {
        return null;
    }

    return {
        title: messageConfig.title,
        message: messageConfig.message,
    };
};

/**
 * Returns true when a cell save for trigger fields should be blocked (below threshold).
 */
export const shouldBlockIdmTriggerFieldSave = (row = {}, fieldKey, behaviorConfig) => {
    const belowThresholdConfig = behaviorConfig?.tier?.autoCalculation?.belowThreshold;
    const triggerFields = getTierTriggerFields(behaviorConfig);

    if (
        !isIdmApiEnabled(behaviorConfig)
        || !isConfigEnabled(belowThresholdConfig)
        || belowThresholdConfig.blockSave === false
        || !triggerFields.includes(fieldKey)
    ) {
        return false;
    }

    return Boolean(getBelowTrackerThresholdError(row, behaviorConfig));
};

/**
 * Resolves the tier control field from selectedDataElement or behavior config.
 */
export const getTierControlField = (selectedDataElement = [], behaviorConfig) => {
    const controlElement = selectedDataElement.find((element) => element.tierControlField === true);
    return controlElement?.value
        ?? controlElement?.name
        ?? behaviorConfig?.tier?.controlField
        ?? TOP_TIER_REQUIRED_FIELD;
};

/**
 * Parses topTierRequired into a numeric tier (0, 1, 2) or null.
 */
export const parseMaxTierFromTopTierValue = (topTierVal) => {
    if (topTierVal == null || topTierVal === '') {
        return null;
    }

    const match = String(topTierVal).match(/tier\s*(\d+)/i);
    return match ? parseInt(match[1], 10) : null;
};

/**
 * Returns tier number from field config (tierLevel or tierN in value/name).
 */
export const getFieldTierNumber = (element = {}) => {
    if (element?.tierLevel != null) {
        return element.tierLevel;
    }

    const fieldKey = element?.value || element?.name || '';
    const match = String(fieldKey).match(/tier(\d+)/i);
    return match ? parseInt(match[1], 10) : null;
};

/**
 * Marks tier control field read-only on column headers when configured.
 */
export const applyIdmTierReadOnlyToColumnHeaders = (
    blockHeaders = [],
    selectedDataElement = [],
    behaviorConfig,
) => {
    const controlField = getTierControlField(selectedDataElement, behaviorConfig);
    const controlFieldReadOnly = behaviorConfig?.tier?.controlFieldReadOnly !== false;

    if (!isConfigEnabled(behaviorConfig?.tier) || !controlFieldReadOnly) {
        return blockHeaders;
    }

    return blockHeaders.map((column) => {
        const fieldKey = column.value || column.name;

        if (column.tierControlField === true || fieldKey === controlField) {
            return { ...column, readOnly: true };
        }

        return column;
    });
};

/**
 * Returns true when a field is editable for the row's current top tier (exact tier match).
 */
export const isFieldEditableForRowTier = (
    fieldKey,
    column = {},
    row = {},
    selectedDataElement = [],
    behaviorConfig,
) => {
    if (!isConfigEnabled(behaviorConfig?.tier?.editability)) {
        return true;
    }

    const editability = behaviorConfig.tier.editability;
    const controlField = getTierControlField(selectedDataElement, behaviorConfig);

    if (column?.tierControlField === true || editability.controlFieldEditable !== true) {
        if (fieldKey === controlField || column?.tierControlField === true) {
            return false;
        }
    }

    const fieldTier = getFieldTierNumber(column);
    if (fieldTier == null) {
        return true;
    }

    const topTierVal = row?.[controlField] ?? row?.topTierRequired;
    const maxTier = parseMaxTierFromTopTierValue(topTierVal);

    if (maxTier == null) {
        return false;
    }

    if (editability.strategy === 'exactTierMatch') {
        return fieldTier === maxTier;
    }

    return fieldTier <= maxTier;
};

/**
 * Interpolates message templates with field context.
 */
const interpolateMessage = (template, values = {}) => (
    String(template).replace(/\{(\w+)\}/g, (_match, key) => (
        values[key] != null ? String(values[key]) : ''
    ))
);

/**
 * User-facing message when a tier-restricted field cannot be edited.
 */
export const getIdmTierRestrictionMessage = (
    column = {},
    row = {},
    selectedDataElement = [],
    behaviorConfig,
) => {
    const controlField = getTierControlField(selectedDataElement, behaviorConfig);
    const topTierVal = row?.[controlField] ?? row?.topTierRequired;
    const maxTier = parseMaxTierFromTopTierValue(topTierVal);
    const fieldTier = getFieldTierNumber(column);
    const fieldLabel = column.label || column.value || column.name || 'This field';
    const messages = behaviorConfig?.tier?.editability?.messages || {};

    if (maxTier == null) {
        return interpolateMessage(messages.noTopTierSet || '{fieldLabel} is not editable until top tier is set.', {
            fieldLabel,
        });
    }

    if (fieldTier != null && fieldTier !== maxTier) {
        return interpolateMessage(
            messages.wrongTier || '{fieldLabel} is only editable when top tier is Tier {tierLevel}.',
            { fieldLabel, tierLevel: fieldTier },
        );
    }

    return interpolateMessage(
        messages.generic || '{fieldLabel} is not editable for the current top tier.',
        { fieldLabel },
    );
};

/**
 * Builds API payload for a cell save, including tier auto-update and tier field clearing.
 */
export const buildIdmCellSavePayload = (
    row = {},
    fieldKey,
    value,
    dataElements = [],
    selectedDataElement = [],
    behaviorConfig,
) => {
    const controlField = getTierControlField(selectedDataElement, behaviorConfig);
    const mergedRow = { ...row, [fieldKey]: value };
    const payload = { [fieldKey]: value };
    const previousTopTierValue = row?.[controlField] ?? row?.topTierRequired;
    const clearOnChange = behaviorConfig?.tier?.clearOnChange;
    const historySuffix = clearOnChange?.historyFieldSuffix || 'History';
    const triggerFields = getTierTriggerFields(behaviorConfig);

    const appendClearedTierFields = (previousValue, nextValue) => {
        if (!isConfigEnabled(clearOnChange)) {
            return;
        }

        const fieldsToClear = getTierFieldsToClearOnTopTierChange(
            previousValue,
            nextValue,
            dataElements,
        );

        fieldsToClear.forEach((fieldName) => {
            payload[fieldName] = null;

            if (clearOnChange.clearHistoryFields !== false) {
                const historyKey = `${fieldName}${historySuffix}`;
                if (Object.prototype.hasOwnProperty.call(row, historyKey)) {
                    payload[historyKey] = null;
                }
            }
        });
    };

    if (
        isIdmApiEnabled(behaviorConfig)
        && isConfigEnabled(behaviorConfig?.tier?.autoCalculation)
        && triggerFields.includes(fieldKey)
    ) {
        const { result: topTierRequired, belowThreshold } = evaluateTierAutoCalculation(mergedRow, behaviorConfig);
        const skipNullUpdate = behaviorConfig.tier.autoCalculation.skipControlFieldUpdateWhenResultIsNull !== false;

        if (!belowThreshold && topTierRequired != null && topTierRequired !== '') {
            appendClearedTierFields(previousTopTierValue, topTierRequired);
            payload[controlField] = topTierRequired;
        } else if (!belowThreshold && !skipNullUpdate && topTierRequired === null) {
            appendClearedTierFields(previousTopTierValue, null);
            payload[controlField] = null;
        }
    }

    if (fieldKey === controlField) {
        appendClearedTierFields(previousTopTierValue, value);
    }

    return payload;
};
